import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TooltipIcon } from "@netapp/bxp-style/react-icons/Notification";
import { CopyReplicateIcon } from "@netapp/bxp-style/react-icons/Action";
import { BulletIcon } from "@netapp/bxp-style/react-icons/General";
import { Notification } from "@netapp/bxp-design-system-react";
import DOMPurify from 'dompurify';
import "../detail.scss";

const Overview = ({ advisoryData, loading }) => {
  const sanitized_ref = advisoryData?.kb_ref.map((ref) => {
    return DOMPurify.sanitize(ref);
  });
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // Function to get the color based on severity
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case "critical":
        return "red";
      case "high":
        return "orange";
      case "medium":
        return "yellow";
      case "low":
        return "green";
      default:
        return "gray";
    }
  };

  // Handler to copy the CVE id to the clipboard
  const handleCopy = (scoring) => {
    if (scoring?.cve_id) {
      navigator.clipboard
        .writeText(scoring.cve_id)
        .then(() => {
          setCopied(true);
          // Hide the notification after 3 seconds
          setTimeout(() => {
            setCopied(false);
          }, 3000);
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    }
  };

  const disclaimerText = t("advisory_single_disclaimer.other").replace('YEAR', new Date().getFullYear());

  return (
    <div className="overview-content">
      {/* Notification positioned at bottom right */}
      {copied && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <Notification type="success">
            {t("advisory_cve_copied_message.other")}
          </Notification>
        </div>
      )}

      <h4>{t("advisory_single_section_summary.other")}</h4>
      <p>
        {loading ? "Loading advisory details..." : advisoryData?.kb_summary}
      </p>
      <h4>{t("advisory_single_section_impact.other")}</h4>
      <p>
        {loading ? "Loading impact details..." : <div dangerouslySetInnerHTML={{ __html: advisoryData?.kb_impact }} />}
      </p>
      <h4>{t("advisory_single_section_vulnScoringDetails.other")}</h4>
      <table
        className="vulnerability-table"
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <thead>
          <tr>
            <th style={{ width: "20%" }}>
              {t("advisory_single_table_cve.other")}
            </th>
            <th style={{ width: "20%" }}>
              {t("advisory_single_table_cvss.other")} {t("advisory_single_table_score.other")}
            </th>
            <th style={{ width: "80%" }}>
              {t("advisory_single_table_vector.other")}
            </th>
          </tr>
        </thead>
        <tbody>
          {advisoryData?.kb_scoring_calc.map((scoring, index) => (
            <tr key={index}>
              <td style={{ width: "20%" }}>
                {loading ? (
                  "Loading..."
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                  {/* CVE ID as NVD link */}
                  {scoring?.cve_id ? (
                    <a
                      href={`https://nvd.nist.gov/vuln/detail/${scoring.cve_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: "bold", color: "#0070c9", textDecoration: "none" }}
                      onMouseOver={e => (e.currentTarget.style.textDecoration = "underline")}
                      onMouseOut={e => (e.currentTarget.style.textDecoration = "none")}
                    >
                      {scoring.cve_id}
                    </a>
                  ) : (
                    <strong>{scoring?.cve_id}</strong>
                  )}                    
                  <CopyReplicateIcon
                      style={{ cursor: "pointer" }}
                      onClick={() => handleCopy(scoring)}
                    />
                  </div>
                )}
              </td>
              <td style={{ width: "20%" }}>
                <BulletIcon
                  style={{
                    marginRight: "4px",
                    verticalAlign: "middle",
                    "--icon-primary": getSeverityColor(scoring?.range),
                  }}
                />
                {loading
                  ? "Loading..."
                  : scoring
                  ? `${scoring.range} (${scoring.score})`
                  : "N/A"}
              </td>
              <td style={{ width: "60%" }}>
                {loading ? "Loading..." : scoring?.vector}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4>{t("advisory_single_section_exploitation.other")}</h4>
      <p>
        {loading
          ? "Loading exploitation details..."
          : advisoryData?.kb_exploitation === "Public"
          ? t("advisory_single_exploitation_aware.other")
          : t("advisory_single_exploitation_unaware.other")}
      </p>

      {/* only display this section when sanitized_ref is not empty */}
      {sanitized_ref && sanitized_ref.length > 0 && (
        <>
          <h4 style={{ marginBottom: "8px" }}>{t("advisory_single_section_references.other")}</h4>
          <div style={{ marginBottom: "12px" }}>
            {loading
              ? "Loading references..."
              : sanitized_ref.map((ref, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    <a
                      href={DOMPurify.sanitize(ref)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0070c9",
                        textDecoration: "none",
                        display: "inline-block",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.textDecoration = "underline")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.textDecoration = "none")
                      }
                    >
                      {DOMPurify.sanitize(ref)}
                    </a>
                  </div>
                ))}
          </div>
        </>
      )}

      {/* Single line gap before the note */}
      <div className="advisory-note" style={{ marginTop: "32px", marginBottom: "12px" }}>
        <p style={{ margin: 0 }}>
          <TooltipIcon
            style={{ verticalAlign: "middle", marginRight: "4px" }}
            tooltip={t("advisory_single_noticeInterim.other")}
          />
          {t("advisory_single_noticeInterim.other")}
        </p>
      </div>
      <div>
        {advisoryData?.kb_acknowledgements && advisoryData.kb_acknowledgements.trim() !== "" && (
          <>
            <h4>{t("advisory_single_section_acknowledgements.other")}</h4>
            <p>
              {loading ? "Loading advisory acknowledgements..." : advisoryData.kb_acknowledgements}
            </p>
          </>
        )}
      </div>
      {/* Reduce gap between last two notes */}
      <div className="advisory-note" style={{ marginTop: 0, marginBottom: "8px" }}>
        <p style={{ margin: 0 }}>
          <strong>{t("advisory_single_upToDate.other")}</strong>
        </p>
      </div>
      <div className="advisory-note" style={{ marginTop: 0 }}>
        <p style={{ margin: 0 }}>{disclaimerText}</p>
      </div>

      {/* ...existing code... (no duplicate acknowledgements or notes here) */}
    </div>
  );
};

export default Overview;
